import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Modal,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AccessController from "_shared/AccessController";
import { DashGridL2 } from "layouts/dash-grid";
import TopBar from "_shared/Top";
import { Toaster } from "react-hot-toast";
import { toastMessage } from "utils/toastMessage";
import {
  getAllSEUsers,
  checkDepotCode,
  addDepotToSE,
  editDepotOnSE,
  deleteDepotFromSE,
} from "_controllers/officer";

const modalStyle = {
  width: "50%",
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "#ffffff",
  border: "none",
  boxShadow: 24,
  pt: 3,
  pb: 3,
  pl: 4,
  pr: 4,
};

const largeModalStyle = {
  ...modalStyle,
  width: "70%",
  maxHeight: "85vh",
  overflowY: "auto",
};

const emptyDepotForm = { depotCode: "", depotName: "", regionCode: "", regionName: "" };
const emptyErrors = { depotCode: "", depotName: "", regionCode: "", regionName: "" };

const Main = () => {
  const [seList, setSeList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const searchTimeout = useRef(null);

  const [selectedSE, setSelectedSE] = useState(null);
  const [depotPanelOpen, setDepotPanelOpen] = useState(false);

  const [addDepotModal, setAddDepotModal] = useState(false);
  const [editDepotModal, setEditDepotModal] = useState(false);

  const [depotForm, setDepotForm] = useState(emptyDepotForm);
  const [formErrors, setFormErrors] = useState(emptyErrors);
  const [submitLoading, setSubmitLoading] = useState(false);

  const [checkResult, setCheckResult] = useState(null);
  const [checkLoading, setCheckLoading] = useState(false);

  const [editingDepot, setEditingDepot] = useState(null);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [depotToDelete, setDepotToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await getAllSEUsers({ page: currentPage, limit: 10, search: debouncedSearch });
        setSeList(res?.data ?? []);
        setTotalPages(res?.pagination?.totalPages ?? 1);
      } catch {
        toastMessage("Failed to load SE list", "error");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentPage, debouncedSearch]);

  const handlePageChange = (_, page) => {
    setCurrentPage(page);
  };

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    setCurrentPage(1);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setDebouncedSearch(val);
    }, 300);
  };

  const openDeleteConfirm = (depot) => {
    setDepotToDelete(depot);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteDepot = async () => {
    setDeleteLoading(true);
    try {
      const res = await deleteDepotFromSE(selectedSE.SAP_ID, depotToDelete.depotCode);
      if (res?.status === "success") {
        toastMessage(res.message ?? "Depot deleted successfully", "success");
        setDeleteConfirmOpen(false);
        setDepotToDelete(null);
        setSelectedSE((prev) => ({
          ...prev,
          depots: prev.depots.filter((d) => d.depotCode !== depotToDelete.depotCode),
        }));
        setSeList((prev) =>
          prev.map((se) =>
            se.SAP_ID === selectedSE.SAP_ID
              ? { ...se, depots: se.depots.filter((d) => d.depotCode !== depotToDelete.depotCode) }
              : se
          )
        );
      } else {
        toastMessage(res?.message ?? "Error deleting depot", "error");
      }
    } catch {
      toastMessage("Failed to delete depot", "error");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSelectSE = (se) => {
    setSelectedSE(se);
    setDepotPanelOpen(true);
  };

  const closeDepotPanel = () => {
    setDepotPanelOpen(false);
    setSelectedSE(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setDepotForm((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
    if (name === "depotCode") setCheckResult(null);
  };

  const handleDepotCodeBlur = async () => {
    if (!depotForm.depotCode.trim()) return;
    setCheckLoading(true);
    try {
      const res = await checkDepotCode(depotForm.depotCode.trim());
      setCheckResult(res);
    } catch {
      // silently ignore — user can still submit
    } finally {
      setCheckLoading(false);
    }
  };

  const validateForm = () => {
    const errs = {};
    if (!depotForm.depotCode.trim()) errs.depotCode = "Depot Code is required";
    if (!depotForm.depotName.trim()) errs.depotName = "Depot Name is required";
    if (!depotForm.regionCode.trim()) errs.regionCode = "Region Code is required";
    if (!depotForm.regionName.trim()) errs.regionName = "Region Name is required";
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const openAddDepot = () => {
    setDepotForm(emptyDepotForm);
    setFormErrors(emptyErrors);
    setCheckResult(null);
    setAddDepotModal(true);
  };

  const openEditDepot = (depot) => {
    setEditingDepot(depot);
    setDepotForm({ ...depot });
    setFormErrors(emptyErrors);
    setEditDepotModal(true);
  };

  const handleAddDepot = async () => {
    if (!validateForm()) return;
    setSubmitLoading(true);
    try {
      const res = await addDepotToSE(selectedSE.SAP_ID, [{ ...depotForm }]);
      if (res?.status === "success") {
        const addedCodes = res.added ?? [];
        const skippedCodes = res.skipped ?? [];
        const msg = skippedCodes.length
          ? `${res.message} (skipped already-assigned: ${skippedCodes.join(", ")})`
          : (res.message ?? "Depot added successfully");
        toastMessage(msg, "success");
        setAddDepotModal(false);
        setDepotForm(emptyDepotForm);
        setCheckResult(null);
        if (addedCodes.length > 0) {
          const newDepot = { ...depotForm };
          setSelectedSE((prev) => ({ ...prev, depots: [...(prev.depots ?? []), newDepot] }));
          setSeList((prev) =>
            prev.map((se) =>
              se.SAP_ID === selectedSE.SAP_ID
                ? { ...se, depots: [...(se.depots ?? []), newDepot] }
                : se
            )
          );
        }
      } else if (res?.status === "error") {
        if (res.invalidItems?.length > 0) {
          const errs = {};
          res.invalidItems.forEach(({ missing }) => {
            missing.forEach((field) => {
              errs[field] = `${field} is required`;
            });
          });
          setFormErrors((prev) => ({ ...prev, ...errs }));
          toastMessage("Please fix the highlighted fields", "error");
        } else {
          toastMessage(res.message ?? "Error adding depot", "error");
        }
      }
    } catch (err) {
      toastMessage(err?.response?.data?.message ?? err?.message ?? "Failed to add depot", "error");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEditDepot = async () => {
    if (!validateForm()) return;
    setSubmitLoading(true);
    try {
      const payload = { seSapId: selectedSE.SAP_ID, depotCode: editingDepot.depotCode };
      if (depotForm.depotName !== editingDepot.depotName) payload.depotName = depotForm.depotName;
      if (depotForm.regionCode !== editingDepot.regionCode) payload.regionCode = depotForm.regionCode;
      if (depotForm.regionName !== editingDepot.regionName) payload.regionName = depotForm.regionName;

      const res = await editDepotOnSE(payload);
      if (res?.status === "success") {
        toastMessage(res.message ?? "Depot updated successfully", "success");
        setEditDepotModal(false);
        setEditingDepot(null);
        const updated = { ...depotForm };
        setSelectedSE((prev) => ({
          ...prev,
          depots: prev.depots.map((d) =>
            d.depotCode === editingDepot.depotCode ? updated : d
          ),
        }));
        setSeList((prev) =>
          prev.map((se) =>
            se.SAP_ID === selectedSE.SAP_ID
              ? {
                  ...se,
                  depots: se.depots.map((d) =>
                    d.depotCode === editingDepot.depotCode ? updated : d
                  ),
                }
              : se
          )
        );
      } else {
        toastMessage(res?.message ?? "Error updating depot", "error");
      }
    } catch {
      toastMessage("Failed to update depot", "error");
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <>
      <div>
        <p style={{ fontSize: "25px", fontWeight: "bolder", color: "darkgrey" }}>
          SE Depot Management
        </p>
      </div>
      <div style={{ width: "98%", border: "1.5px solid grey", marginBottom: "1rem" }} />

      <Box sx={{ mb: 2, maxWidth: 320 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search SAP ID"
          value={search}
          onChange={handleSearchChange}
        />
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bolder" }}>SAP ID</TableCell>
                <TableCell sx={{ fontWeight: "bolder" }}>Name</TableCell>
                <TableCell sx={{ fontWeight: "bolder" }}>Email</TableCell>
                <TableCell sx={{ fontWeight: "bolder" }}>Phone</TableCell>
                <TableCell sx={{ fontWeight: "bolder" }}>Region</TableCell>
                <TableCell sx={{ fontWeight: "bolder" }}>Status</TableCell>
                <TableCell sx={{ fontWeight: "bolder" }}>Depots</TableCell>
                <TableCell sx={{ fontWeight: "bolder" }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {seList.length > 0 ? (
                seList.map((se) => (
                  <TableRow key={se.SAP_ID} hover>
                    <TableCell>{se.SAP_ID}</TableCell>
                    <TableCell>{se.name}</TableCell>
                    <TableCell>{se.email}</TableCell>
                    <TableCell>{se.phone}</TableCell>
                    <TableCell>
                      {se.regionName} ({se.regionCode})
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={se.isActive ? "Active" : "Inactive"}
                        color={se.isActive ? "success" : "default"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip label={`${se.depots?.length ?? 0} depot(s)`} size="small" />
                    </TableCell>
                    <TableCell>
                      <Button size="small" variant="outlined" onClick={() => handleSelectSE(se)}>
                        Manage Depots
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body1" sx={{ p: 2 }}>
                      No SE users found.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <Grid container justifyContent="center" p={2}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
            />
          </Grid>
        </TableContainer>
      )}

      {/* Depot panel */}
      <Modal open={depotPanelOpen} onClose={closeDepotPanel}>
        <Box sx={largeModalStyle}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h5">
              {selectedSE?.name}
              <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                {selectedSE?.SAP_ID}
              </Typography>
            </Typography>
            <Button variant="contained" onClick={openAddDepot}>
              + Add Depot
            </Button>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bolder" }}>Depot Code</TableCell>
                  <TableCell sx={{ fontWeight: "bolder" }}>Depot Name</TableCell>
                  <TableCell sx={{ fontWeight: "bolder" }}>Region Code</TableCell>
                  <TableCell sx={{ fontWeight: "bolder" }}>Region Name</TableCell>
                  <TableCell sx={{ fontWeight: "bolder" }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedSE?.depots?.length > 0 ? (
                  selectedSE.depots.map((depot) => (
                    <TableRow key={depot.depotCode}>
                      <TableCell>{depot.depotCode}</TableCell>
                      <TableCell>{depot.depotName}</TableCell>
                      <TableCell>{depot.regionCode}</TableCell>
                      <TableCell>{depot.regionName}</TableCell>
                      <TableCell>
                        <EditIcon
                          sx={{ cursor: "pointer", color: "green", mr: 1 }}
                          onClick={() => openEditDepot(depot)}
                        />
                        <DeleteIcon
                          sx={{ cursor: "pointer", color: "red" }}
                          onClick={() => openDeleteConfirm(depot)}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography variant="body2" sx={{ p: 1 }}>
                        No depots assigned yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
            <Button variant="outlined" color="error" onClick={closeDepotPanel}>
              Close
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Add Depot modal */}
      <Modal
        open={addDepotModal}
        onClose={() => {
          setAddDepotModal(false);
          setDepotForm(emptyDepotForm);
          setFormErrors(emptyErrors);
          setCheckResult(null);
        }}
      >
        <Box sx={modalStyle}>
          <Typography variant="h5" sx={{ textAlign: "center", mb: 3 }}>
            Add Depot
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box>
              <TextField
                fullWidth
                label="Depot Code"
                name="depotCode"
                value={depotForm.depotCode}
                onChange={handleFormChange}
                onBlur={handleDepotCodeBlur}
                error={!!formErrors.depotCode}
                helperText={formErrors.depotCode}
                InputProps={{
                  endAdornment: checkLoading ? (
                    <CircularProgress size={18} />
                  ) : checkResult?.status === "available" ? (
                    <CheckCircleIcon color="success" />
                  ) : null,
                }}
              />
              {checkResult?.status === "available" && (
                <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: "block" }}>
                  {checkResult.message}
                </Typography>
              )}
              {checkResult?.status === "assigned" && (
                <Alert severity="info" sx={{ mt: 0.5 }}>
                  Already held by{" "}
                  {checkResult.assignedTo
                    ?.map((se) => `(${se.SAP_ID})`)
                    .join(", ")}{" "}
                  — you can still add it
                </Alert>
              )}
            </Box>
            <TextField
              fullWidth
              label="Depot Name"
              name="depotName"
              value={depotForm.depotName}
              onChange={handleFormChange}
              error={!!formErrors.depotName}
              helperText={formErrors.depotName}
            />
            <TextField
              fullWidth
              label="Region Code"
              name="regionCode"
              value={depotForm.regionCode}
              onChange={handleFormChange}
              error={!!formErrors.regionCode}
              helperText={formErrors.regionCode}
            />
            <TextField
              fullWidth
              label="Region Name"
              name="regionName"
              value={depotForm.regionName}
              onChange={handleFormChange}
              error={!!formErrors.regionName}
              helperText={formErrors.regionName}
            />
          </Box>
          <Box sx={{ mt: 3, display: "flex", justifyContent: "space-evenly" }}>
            <Button
              variant="contained"
              color="error"
              onClick={() => {
                setAddDepotModal(false);
                setDepotForm(emptyDepotForm);
                setFormErrors(emptyErrors);
                setCheckResult(null);
              }}
            >
              Cancel
            </Button>
            <Button variant="contained" onClick={handleAddDepot} disabled={submitLoading}>
              {submitLoading ? <CircularProgress size={20} color="inherit" /> : "Add Depot"}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Edit Depot modal */}
      <Modal
        open={editDepotModal}
        onClose={() => {
          setEditDepotModal(false);
          setDepotForm(emptyDepotForm);
          setFormErrors(emptyErrors);
          setEditingDepot(null);
        }}
      >
        <Box sx={modalStyle}>
          <Typography variant="h5" sx={{ textAlign: "center", mb: 3 }}>
            Edit Depot — {editingDepot?.depotCode}
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              fullWidth
              label="Depot Code"
              name="depotCode"
              value={depotForm.depotCode}
              disabled
            />
            <TextField
              fullWidth
              label="Depot Name"
              name="depotName"
              value={depotForm.depotName}
              onChange={handleFormChange}
              error={!!formErrors.depotName}
              helperText={formErrors.depotName}
            />
            <TextField
              fullWidth
              label="Region Code"
              name="regionCode"
              value={depotForm.regionCode}
              onChange={handleFormChange}
              error={!!formErrors.regionCode}
              helperText={formErrors.regionCode}
            />
            <TextField
              fullWidth
              label="Region Name"
              name="regionName"
              value={depotForm.regionName}
              onChange={handleFormChange}
              error={!!formErrors.regionName}
              helperText={formErrors.regionName}
            />
          </Box>
          <Box sx={{ mt: 3, display: "flex", justifyContent: "space-evenly" }}>
            <Button
              variant="contained"
              color="error"
              onClick={() => {
                setEditDepotModal(false);
                setDepotForm(emptyDepotForm);
                setFormErrors(emptyErrors);
                setEditingDepot(null);
              }}
            >
              Cancel
            </Button>
            <Button variant="contained" onClick={handleEditDepot} disabled={submitLoading}>
              {submitLoading ? <CircularProgress size={20} color="inherit" /> : "Update Depot"}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Delete confirmation modal */}
      <Modal
        open={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setDepotToDelete(null);
        }}
      >
        <Box sx={{ ...modalStyle, width: "35%" }}>
          <Typography variant="h6" sx={{ textAlign: "center", mb: 2 }}>
            Delete Depot
          </Typography>
          <Typography variant="body1" sx={{ textAlign: "center", mb: 3 }}>
            Are you sure you want to delete depot{" "}
            <strong>{depotToDelete?.depotCode}</strong> —{" "}
            {depotToDelete?.depotName}?
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "space-evenly" }}>
            <Button
              variant="outlined"
              onClick={() => {
                setDeleteConfirmOpen(false);
                setDepotToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDeleteDepot}
              disabled={deleteLoading}
            >
              {deleteLoading ? <CircularProgress size={20} color="inherit" /> : "Delete"}
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

const SEDepot = () => {
  return (
    <AccessController roles={["ADMIN"]}>
      <DashGridL2 Top={TopBar} Main={Main} />
      <Toaster />
    </AccessController>
  );
};

export default SEDepot;
